/**
 * Тренд: идти туда, куда рынок уже идёт.
 *
 * Общая беда трендовых идей — боковик: там средние пересекаются туда и
 * обратно, и каждое пересечение даёт ложный вход. Поэтому у идей раздела
 * сила тренда (ADX) стоит в обязательном фоне, а не среди усилителей.
 */
import { above, adx, atr, breakOut, close, divergenceParams, ema, happened, ind, mirror, number, previous, recently, rvol, scaled, below, spec, structureTrend, swingParams, vwap, weight, } from './kit.js';
export const TREND_TEMPLATES = [
    {
        id: 'trend',
        name: 'EMA Cross',
        icon: '📈',
        category: 'trend',
        popular: true,
        hint: 'Вход по ходу крупного тренда на пересечении скользящих средних.',
        steps: ['Цена по нужную сторону от EMA 200', 'EMA 50 пересекла EMA 200', 'Сила тренда'],
        keywords: ['ema', 'скользящая', 'пересечение', 'золотой крест'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Быстрая средняя (50) пересекает медленную (200): короткая история цены разворачивается вслед за длинной.',
            why: 'Средние сглаживают шум, и их пересечение отмечает смену крупного направления, а не случайный всплеск.',
            fails: 'В боковике средние сплетаются и пересекаются туда-обратно; сигнал приходит поздно — часть движения уже прошла.',
            tip: 'Редкий сигнал: на 4ч и дне — несколько раз в год. Ценен как фильтр направления для других идей.',
        },
        build: (o) => spec(o, 'EMA Cross', mirror((side) => ({
            context: [side.beyond(close, ema(200))],
            trigger: side.cross(ema(50), ema(200)),
            confirmations: [weight('c1', above(adx, number(25)), 100)],
        })), 0),
    },
    {
        id: 'trend-following',
        name: 'Следование тренду',
        icon: '📈',
        category: 'trend',
        popular: true,
        hint: 'Крупный тренд задаёт сторону, вход — по возврату цены к быстрой средней.',
        steps: ['Крупный тренд по нужную сторону', 'Тренд достаточно силён', 'Цена вернулась выше EMA 20', 'Объём'],
        keywords: ['тренд', 'ema', 'adx'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Крупный тренд уже идёт, цена ненадолго провалилась под быструю среднюю и снова её пересекла — движение продолжается.',
            why: 'Внутри тренда откаты короткие: покупатели (или продавцы) возвращаются, как только цена стала «дешевле».',
            fails: 'Когда тренд выдыхается: откат превращается в разворот, а сигнал приходит на последнем подъёме.',
            tip: 'Сила тренда (ADX выше 20) — обязательное условие: без неё идея превращается в пилу.',
        },
        build: (o) => spec(o, 'Следование тренду', mirror((side) => ({
            context: [side.beyond(close, ema(200)), side.beyond(ema(50), ema(200)), above(adx, number(20))],
            trigger: side.cross(close, ema(20)),
            confirmations: [weight('c1', above(adx, number(25)), 50), weight('c2', above(rvol, number(1.1)), 50)],
        })), 0),
    },
    {
        id: 'pullback',
        name: 'Вход на откате',
        icon: '↩️',
        category: 'trend',
        popular: true,
        hint: 'Тренд, откат к средней и разворот обратно — вход туда, где движение возобновляется.',
        steps: ['Есть тренд по нужную сторону', 'Структура недавно развернулась в нашу сторону', 'Цена вернулась к EMA 20', 'Свеча разворота и объём'],
        keywords: ['откат', 'pullback', 'коррекция'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Структура рынка развернулась в нашу сторону, цена откатилась к средней и пошла обратно — вход на продолжении.',
            why: 'Первый откат после смены структуры — место, где новые участники входят по лучшей цене.',
            fails: 'Если смена структуры была ложной: откат продолжается и ломает её обратно.',
            tip: 'Поглощение на откате — самый сильный усилитель здесь: он показывает, что сторона вернулась.',
        },
        build: (o) => spec(o, 'Вход на откате', mirror((side) => ({
            context: [side.beyond(close, ema(200)), recently(ind('choch', swingParams, side.way), 30)],
            trigger: side.cross(close, ema(20)),
            confirmations: [
                weight('c1', happened(ind('engulfing', { factor: 1 }, side.way)), 40),
                weight('c2', above(rvol, number(1.2)), 30),
                weight('c3', above(adx, number(20)), 30),
            ],
        })), 30),
    },
    {
        id: 'momentum-continuation',
        name: 'Продолжение импульса',
        icon: '🚀',
        category: 'trend',
        hint: 'Цена уже идёт и обновляет крайнюю точку двадцати баров — идём следом.',
        steps: ['Скорость изменения в нашу сторону', 'Цена по нужную сторону от EMA 50', 'Пробит экстремум 20 баров', 'Объём'],
        keywords: ['импульс', 'momentum', 'roc'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '1D',
            idea: 'Цена уже ускоряется в одну сторону и выходит за крайнюю точку последних 20 баров.',
            why: 'Рынок, который обновляет экстремумы, часто продолжает их обновлять: за ним идут новые покупатели и закрываются чужие стопы.',
            fails: 'На последнем рывке перед разворотом и в узком боковике, где «пробой» тут же возвращается.',
            tip: 'Лучше работает на дне и 4ч: на младших таймфреймах экстремум 20 баров — это шум.',
        },
        build: (o) => spec(o, 'Продолжение импульса', mirror((side) => ({
            context: [side.beyond(ind('roc', { period: 9 }), number(0)), side.beyond(close, ema(50))],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 0),
    },
    {
        id: 'supertrend',
        name: 'Supertrend',
        icon: '🧭',
        category: 'trend',
        hint: 'Вход на перевороте линии Supertrend, когда у рынка есть сила.',
        steps: ['Тренд достаточно силён', 'Цена пересекла Supertrend', 'Объём'],
        keywords: ['supertrend', 'atr'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '1D',
            idea: 'Линия Supertrend следует за ценой на расстоянии обычного размаха; пересечение означает смену стороны.',
            why: 'Расстояние в размахах (ATR) отсекает мелкие колебания: линию пробивает только заметное движение.',
            fails: 'В боковике линия переворачивается туда-обратно, и каждый переворот — убыток.',
            tip: 'Фильтр ADX выше 18 убирает большую часть боковых переворотов.',
        },
        build: (o) => spec(o, 'Supertrend', mirror((side) => ({
            context: [above(adx, number(18))],
            trigger: side.cross(close, ind('supertrend', { period: 10, multiplier: 3 })),
            confirmations: [weight('c1', above(adx, number(25)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 0),
    },
    {
        id: 'divergence-hidden',
        name: 'Скрытая дивергенция',
        icon: '🔄',
        category: 'trend',
        hint: 'Продолжение текущего тренда после коррекции: откат цены мельче, чем просел импульс.',
        steps: ['Тренд по структуре в нашу сторону', 'Откат мельче, чем просел импульс', 'Сила тренда и объём'],
        keywords: ['скрытая', 'дивергенция', 'продолжение', 'откат', 'тренд'],
        passport: {
            level: 'pro',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'В тренде цена сделала мелкий откат, а RSI при этом упал глубоко — это передышка, а не разворот.',
            why: 'Импульс «перезарядился» на откате, а цена почти не потеряла — у тренда остались силы.',
            fails: 'Вне тренда та же картинка — просто шум; поэтому направление структуры здесь обязательно.',
            tip: 'Хорошо дополняет «Вход на откате»: одна идея ищет место, другая — подтверждение силы.',
        },
        build: (o) => spec(o, 'Скрытая дивергенция', mirror((side) => ({
            context: [side.sign > 0 ? above(structureTrend(), number(0)) : below(structureTrend(), number(0))],
            trigger: happened(ind('divergence', divergenceParams, `hidden_${side.way}`)),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'ema-fan',
        name: 'Веер EMA 8/21/55',
        icon: '🪭',
        category: 'trend',
        hint: 'Три средние выстроились веером по ходу тренда — вход, когда цена возвращается к средней 21.',
        steps: ['EMA 8 выше EMA 21 выше EMA 55', 'Цена откатилась и снова пересекла EMA 21', 'Сила тренда'],
        keywords: ['ema', 'веер', 'три средние', 'выравнивание'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Быстрая, средняя и медленная EMA выстроились по порядку — это «чистый» тренд. Вход — на возврате к средней линии.',
            why: 'Когда средние идут веером, откаты мелкие и короткие: рынок не успевает развернуться.',
            fails: 'Когда веер начинает сжиматься и линии сближаются — тренд заканчивается.',
            tip: 'Смотрите на наклон: крутой веер надёжнее пологого.',
        },
        build: (o) => spec(o, 'Веер EMA 8/21/55', mirror((side) => ({
            context: [side.beyond(ema(8), ema(21)), side.beyond(ema(21), ema(55))],
            trigger: side.cross(close, ema(21)),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'vwap-trend',
        name: 'Откат к VWAP в тренде',
        icon: '🎚',
        category: 'trend',
        hint: 'В сильном тренде цена откатывается к средней по объёму и уходит обратно.',
        steps: ['EMA 50 по нужную сторону от EMA 200', 'Тренд силён', 'Цена снова пересекла VWAP', 'Объём'],
        keywords: ['vwap', 'откат', 'средняя по объёму'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '5m',
            idea: 'VWAP — средняя цена с учётом объёма. В тренде крупные участники охотно добирают позицию около неё.',
            why: 'Для многих фондов и алгоритмов VWAP — «справедливая» цена: ниже неё в растущем рынке покупают.',
            fails: 'Когда тренд слабеет, цена проходит VWAP насквозь и не возвращается.',
            tip: 'Работает внутри дня; на 4ч и дне VWAP уже почти не отличается от обычной средней.',
        },
        build: (o) => spec(o, 'Откат к VWAP в тренде', mirror((side) => ({
            context: [side.beyond(ema(50), ema(200)), above(adx, number(20))],
            trigger: side.cross(close, vwap),
            confirmations: [weight('c1', above(rvol, number(1.2)), 60), weight('c2', side.beyond(close, ema(200)), 40)],
        })), 0),
    },
    {
        id: 'squeeze-momentum',
        name: 'Импульс после сжатия',
        icon: '🎯',
        category: 'trend',
        hint: 'Рынок долго затихал, затем вышел из диапазона — тишина закончилась в нашу сторону.',
        steps: ['Несколько баров назад размах был заметно ниже обычного', 'Пробит экстремум 20 баров', 'Объём'],
        keywords: ['сжатие', 'squeeze', 'импульс', 'atr'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '1D',
            idea: 'Сначала рынок сжимается — свечи мельчают. Потом цена выходит за границу диапазона: накопленная энергия высвобождается.',
            why: 'После затишья движения обычно сильнее: позиции копились, стопы стоят плотно у границ.',
            fails: 'Первый выход из сжатия бывает ложным — цена выходит и тут же возвращается.',
            tip: 'Объём на пробое — главный отличитель настоящего выхода от ложного.',
        },
        build: (o) => spec(o, 'Импульс после сжатия', mirror((side) => ({
            context: [below(previous(atr(14), 5), scaled(atr(50), 0.75))],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(rvol, number(1.5)), 70), weight('c2', side.beyond(close, ema(50)), 30)],
        })), 0),
    },
];
//# sourceMappingURL=trend.js.map